import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin } from "lucide-react";
import api, { API } from "../lib/api";
import { Monogram } from "../components/Monogram";
import { classDayTime } from "../lib/format";

const TANNA = "tannafrederick1@gmail.com";
const MAILTO_TAIL = " with your name and phone number and we will save your spot.";

const isPreviewHost = () => {
  const h = window.location.hostname;
  return h.endsWith("emergentagent.com") || h === "localhost";
};

export default function Join() {
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", note: "", website: "", class_id: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [openClasses, setOpenClasses] = useState([]);
  const [showForm, setShowForm] = useState(null); // null = setting not known yet → render nothing in the slot

  useEffect(() => {
    api.get("/public/classes?program=whitefire")
      .then((r) => setOpenClasses(r.data.filter((c) => c.open_for_signups && c.program === "whitefire")))
      .catch(() => {});
    if (isPreviewHost() && new URLSearchParams(window.location.search).get("preview_form") === "1") {
      setShowForm(true);
      return;
    }
    api.get("/public/site-settings", { timeout: 3000 })
      .then((r) => setShowForm(r.data?.show_join_form === true))
      .catch(() => setShowForm(false));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Lands even when CORS is broken: no-cors + text/plain = no preflight, no custom headers.
  const reportAttempt = (kind, payload, extra) => {
    try {
      fetch(`${API}/public/signup-attempts/client-report`, {
        method: "POST", mode: "no-cors", keepalive: true,
        headers: { "Content-Type": "text/plain" },
        body: JSON.stringify({
          kind, url: window.location.href, user_agent: navigator.userAgent,
          name: payload.full_name, email: payload.email, phone: payload.phone, note: payload.note,
          class_id: payload.class_id || null, ...extra,
        }),
      }).catch(() => {});
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    setEmailError("");
    const payload = { ...form };
    if (openClasses.length < 2) delete payload.class_id;
    else if (!payload.class_id) delete payload.class_id;
    try {
      await api.post("/public/signups", payload, { timeout: 15000 });
      setDone(true);
    } catch (err) {
      if (!err.response) {
        // network error / CORS block / 15 s timeout — no HTTP response at all
        reportAttempt("network_fallback", payload, { error: err.code ? `${err.code}: ${err.message}` : err.message || "network error" });
        setError("That didn't go through — please email Tanna at ");
        return;
      }
      const detail = err.response?.data?.detail;
      const mentionsEmail =
        (Array.isArray(detail) && detail.some((d) => (d.loc || []).includes("email"))) ||
        (typeof detail === "string" && detail.toLowerCase().includes("email"));
      if (err.response.status === 422 && mentionsEmail) {
        setEmailError("Please enter a valid email address."); // server already recorded this as validation_error
        return;
      }
      const message = typeof detail === "string" ? detail : JSON.stringify(detail ?? err.response.data ?? "");
      reportAttempt("client_report", payload, { status: err.response.status, error: message });
      if (typeof detail === "string" && detail === "Please choose a class.") {
        setError("Please choose a class. Or email Tanna at ");
      } else {
        setError("That didn't go through — please try again. Or email Tanna at ");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="border-b border-hairline">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 flex h-16 items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="join-nav-home-logo">
            <Monogram size={40} />
            <span className="font-serif text-lg" style={{ fontWeight: 600 }}>
              Working Actor
            </span>
          </Link>
          <Link to="/" className="btn-ghost text-sm" data-testid="join-nav-home">
            Home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 sm:px-6 py-16 sm:py-20">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="overline mb-4">Online · Live, from anywhere</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink tracking-tight leading-[0.95]">
              Sunday Scene Study
            </h1>
            <p className="font-sans text-base text-ink/80 leading-relaxed mt-5 max-w-md">
              Coached by Eliza Roberts &amp; Tanna Frederick — featuring Eric Roberts &amp; Sean Paul Payton
            </p>
            <div className="mt-8 flex flex-col gap-2" data-testid="join-facts">
              <p className="font-mono text-sm text-ink flex items-start gap-2" data-testid="join-fact-where">
                <MapPin size={14} className="text-brass shrink-0 mt-1" />
                <span>
                  Where — Online
                  <span className="block font-sans text-xs text-stone" data-testid="join-fact-where-subline">Live, from anywhere</span>
                </span>
              </p>
              <p className="font-mono text-sm text-stone" data-testid="join-fact-when">
                When — Sundays, 10:00 AM – 1:00 PM Pacific
              </p>
              <p className="font-mono text-sm text-stone" data-testid="join-fact-start">
                Now — November enrollment open
              </p>
            </div>
            <div className="mt-10 border border-hairline p-3 bg-white max-w-md" data-testid="join-flyer">
              <img src="/wa-flyer-november.png" alt="Working Actor — November enrollment open: weekly Sunday acting classes, live on Zoom — Tanna Frederick, Eric Roberts, Eliza Roberts, Sean Paul Payton" className="w-full h-auto" loading="lazy" data-testid="join-flyer-image" />
            </div>
          </div>

          <div className="lg:col-span-7">
            {showForm !== null && (
            <div className="paper-card p-6 sm:p-8 max-w-xl bg-white">
              {!showForm ? (
                <p className="font-mono text-base text-ink leading-relaxed" data-testid="join-email-contact">
                  To join The Working Actor, email Tanna at <a href="mailto:tannafrederick1@gmail.com" className="text-brass underline" data-testid="join-email-contact-link">tannafrederick1@gmail.com</a> with your name and phone number, and we will save your spot.
                </p>
              ) : done ? (
                <p className="font-serif text-2xl text-ink leading-snug" data-testid="class-signup-success">
                  Got it — we have your name. We'll be in touch to confirm your spot.
                </p>
              ) : (
                <form onSubmit={submit} data-testid="class-signup-form">
                  <div className="space-y-5">
                    <div>
                      <label className="field-label">Full name</label>
                      <input
                        className="field-input"
                        value={form.full_name}
                        onChange={set("full_name")}
                        required
                        maxLength={200}
                        data-testid="signup-full-name-input"
                      />
                    </div>
                    <div>
                      <label className="field-label">Email</label>
                      <input
                        type="email"
                        className="field-input"
                        value={form.email}
                        onChange={set("email")}
                        required
                        maxLength={200}
                        data-testid="signup-email-input"
                      />
                      {emailError && (
                        <p className="font-sans text-xs text-garnet mt-1" data-testid="signup-email-error">{emailError}</p>
                      )}
                    </div>
                    <div>
                      <label className="field-label">Phone (optional)</label>
                      <input
                        type="tel"
                        className="field-input"
                        value={form.phone}
                        onChange={set("phone")}
                        maxLength={200}
                        data-testid="signup-phone-input"
                      />
                    </div>
                    <div>
                      <label className="field-label">Anything we should know? (optional)</label>
                      <textarea
                        className="field-input min-h-[96px]"
                        value={form.note}
                        onChange={set("note")}
                        maxLength={1000}
                        data-testid="signup-note-input"
                      />
                      <p className="font-sans text-xs text-stone mt-1 text-right" data-testid="signup-note-counter">{form.note.length} / 1000</p>
                    </div>
                    {openClasses.length >= 2 && (
                      <div data-testid="class-radio-group">
                        <label className="field-label">Which class?</label>
                        <div className="space-y-2 mt-1">
                          {openClasses.map((c) => (
                            <label key={c.id} className="flex items-center gap-2 font-sans text-sm text-ink cursor-pointer">
                              <input
                                type="radio"
                                name="class_id"
                                required
                                value={c.id}
                                checked={form.class_id === c.id}
                                onChange={set("class_id")}
                                data-testid={`class-radio-${c.id}`}
                              />
                              {c.title} · {classDayTime(c)}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                    <input
                      type="text"
                      value={form.website}
                      onChange={set("website")}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: "absolute", left: "-9999px", height: 0, width: 0, opacity: 0 }}
                    />
                  </div>
                  {error && (
                    <p className="font-sans text-sm text-garnet mt-4" data-testid="class-signup-error">
                      {error}<a href={`mailto:${TANNA}`} className="underline" data-testid="class-signup-error-mailto">{TANNA}</a>{MAILTO_TAIL}
                    </p>
                  )}
                  <button type="submit" className="btn-brass mt-6 w-full sm:w-auto" disabled={busy} data-testid="class-signup-submit">
                    {busy ? "Sending…" : "Save my spot"}
                  </button>
                  <p className="font-sans text-xs text-stone mt-4">
                    We'll only use this to contact you about the class.
                  </p>
                </form>
              )}
            </div>
            )}
          </div>
        </div>
        <p className="font-sans text-xs text-stone leading-relaxed mt-16 sm:mt-20" data-testid="join-employment-disclaimer">
          <strong className="font-semibold">Employment Disclaimer:</strong> Working Actor is an educational acting class designed for training and professional development. Participation in the class does not constitute an audition or offer of employment and does not guarantee employment, representation, auditions, casting opportunities, or any other professional engagement. Tuition is paid solely for instruction and participation in the class.
        </p>
      </main>

      <footer className="bg-paper border-t border-hairline">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Monogram size={36} />
            <span className="font-serif text-base text-ink" style={{ fontWeight: 600 }}>
              Working Actor
            </span>
          </div>
          <p className="font-sans text-xs text-stone">
            © {new Date().getFullYear()} The Acting Studio of Eliza Roberts.
          </p>
        </div>
      </footer>
    </div>
  );
}
